import React, { useState, useEffect, useCallback } from 'react';
import { LexAIService } from '../services/api';
import { SharePointItem, SharePointBreadcrumb } from '../types/api';

interface SharePointFileExplorerProps {
  onFileSelect?: (file: SharePointItem, path: string) => void;
  onFileDoubleClick?: (file: SharePointItem, path: string) => void;
  className?: string;
}

const SharePointFileExplorer: React.FC<SharePointFileExplorerProps> = ({
  onFileSelect,
  onFileDoubleClick,
  className = ''
}) => {
  const [items, setItems] = useState<SharePointItem[]>([]);
  const [breadcrumbs, setBreadcrumbs] = useState<SharePointBreadcrumb[]>([]);
  const [currentPath, setCurrentPath] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

  const loadFolder = useCallback(async (path: string) => {
    setLoading(true);
    setError(null);
    setSelectedItemId(null);

    try {
      const response = await LexAIService.browseSharePoint(path || undefined);

      if (response && response.success) {
        setItems(response.items);
        setBreadcrumbs(response.breadcrumbs);
        setCurrentPath(response.currentPath);
      } else {
        setError('Failed to load folder contents');
        setItems([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load folder');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFolder('');
  }, [loadFolder]);

  const handleItemClick = useCallback((item: SharePointItem) => {
    setSelectedItemId(item.id);
    onFileSelect?.(item, currentPath);
  }, [currentPath, onFileSelect]);

  const handleItemDoubleClick = useCallback((item: SharePointItem) => {
    if (item.isFolder) {
      const newPath = currentPath ? `${currentPath}/${item.name}` : item.name;
      loadFolder(newPath);
    } else {
      onFileDoubleClick?.(item, currentPath);
    }
  }, [currentPath, loadFolder, onFileDoubleClick]);

  const handleBreadcrumbClick = useCallback((breadcrumb: SharePointBreadcrumb) => {
    loadFolder(breadcrumb.path);
  }, [loadFolder]);

  const getFileIcon = (item: SharePointItem): string => {
    if (item.isFolder) return 'folder';
    switch (item.extension) {
      case '.pdf': return 'picture_as_pdf';
      case '.doc': case '.docx': return 'article';
      case '.xls': case '.xlsx': return 'table_chart';
      case '.ppt': case '.pptx': return 'slideshow';
      case '.jpg': case '.jpeg': case '.png': case '.gif': case '.webp': return 'image';
      case '.md': case '.txt': return 'text_snippet';
      case '.csv': return 'grid_on';
      default: return 'description';
    }
  };

  const getFileIconColor = (item: SharePointItem): string => {
    if (item.isFolder) return 'var(--obsidian-folder-color, #8b949e)';
    switch (item.extension) {
      case '.pdf': return '#e74c3c';
      case '.doc': case '.docx': return '#2b579a';
      case '.xls': case '.xlsx': return '#217346';
      case '.ppt': case '.pptx': return '#d04423';
      case '.jpg': case '.jpeg': case '.png': case '.gif': case '.webp': return '#9b59b6';
      case '.md': return '#7c3aed';
      default: return 'var(--obsidian-text-secondary)';
    }
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr?: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffHours < 1) return 'Now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 48) return '1d ago';
    if (diffHours < 168) return `${Math.floor(diffHours / 24)}d ago`;
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  };

  return (
    <div className={`sharepoint-file-explorer ${className}`} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Breadcrumb navigation */}
      <div style={{
        padding: '6px 12px',
        borderBottom: '1px solid var(--obsidian-border)',
        fontSize: '12px',
        color: 'var(--obsidian-text-secondary)',
        backgroundColor: 'var(--obsidian-bg-secondary)',
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        flexWrap: 'wrap',
        minHeight: '32px'
      }}>
        <span className="material-symbols-outlined" style={{ fontSize: '14px', color: 'var(--obsidian-accent)', flexShrink: 0 }}>cloud</span>
        {breadcrumbs.map((crumb, index) => (
          <React.Fragment key={crumb.path + index}>
            {index > 0 && (
              <span className="material-symbols-outlined" style={{ fontSize: '14px', color: 'var(--obsidian-text-disabled)' }}>
                chevron_right
              </span>
            )}
            <button
              onClick={() => handleBreadcrumbClick(crumb)}
              style={{
                background: 'none',
                border: 'none',
                color: index === breadcrumbs.length - 1 ? 'var(--obsidian-text-primary)' : 'var(--obsidian-accent)',
                cursor: index === breadcrumbs.length - 1 ? 'default' : 'pointer',
                padding: '2px 4px',
                borderRadius: '3px',
                fontSize: '12px',
                fontWeight: index === breadcrumbs.length - 1 ? 600 : 400,
                transition: 'background-color 0.15s'
              }}
              onMouseEnter={(e) => {
                if (index < breadcrumbs.length - 1) {
                  (e.target as HTMLElement).style.backgroundColor = 'var(--obsidian-hover-bg)';
                }
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.backgroundColor = 'transparent';
              }}
              title={crumb.name}
            >
              {crumb.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {/* Content area */}
      <div style={{
        flex: 1,
        overflowX: 'hidden',
        overflowY: 'auto',
        padding: '4px'
      }}>
        {loading && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
            color: 'var(--obsidian-text-secondary)'
          }}>
            <div className="loading-dots">
              <div className="loading-dot"></div>
              <div className="loading-dot"></div>
              <div className="loading-dot"></div>
            </div>
            <span style={{ marginLeft: '12px', fontSize: '13px' }}>Loading files...</span>
          </div>
        )}

        {error && (
          <div style={{
            padding: '16px',
            textAlign: 'center',
            color: 'var(--obsidian-text-secondary)',
            fontSize: '13px'
          }}>
            <span className="material-symbols-outlined" style={{
              fontSize: '32px',
              display: 'block',
              marginBottom: '8px',
              color: 'var(--obsidian-text-disabled)'
            }}>
              cloud_off
            </span>
            <p style={{ marginBottom: '8px' }}>{error}</p>
            <button
              onClick={() => loadFolder(currentPath)}
              style={{
                background: 'var(--obsidian-accent)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                padding: '6px 12px',
                cursor: 'pointer',
                fontSize: '12px'
              }}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '32px 16px',
            color: 'var(--obsidian-text-secondary)',
            fontSize: '13px'
          }}>
            <span className="material-symbols-outlined" style={{
              fontSize: '40px',
              display: 'block',
              marginBottom: '8px',
              opacity: 0.5
            }}>
              folder_open
            </span>
            This folder is empty
          </div>
        )}

        {!loading && !error && items.map((item) => {
          const isSelected = selectedItemId === item.id;
          return (
            <div
              key={item.id}
              onClick={() => handleItemClick(item)}
              onDoubleClick={() => handleItemDoubleClick(item)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '5px 8px',
                cursor: 'pointer',
                borderRadius: '4px',
                backgroundColor: isSelected ? 'var(--obsidian-accent-bg)' : 'transparent',
                border: isSelected ? '1px solid var(--obsidian-accent)' : '1px solid transparent',
                userSelect: 'none',
                transition: 'background-color 0.15s',
                marginBottom: '1px'
              }}
              onMouseEnter={(e) => {
                if (!isSelected) {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--obsidian-hover-bg)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isSelected) {
                  (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent';
                }
              }}
              title={item.name}
            >
              {/* Icon */}
              <span
                className="material-symbols-outlined"
                style={{
                  fontSize: '18px',
                  color: getFileIconColor(item),
                  flexShrink: 0
                }}
              >
                {getFileIcon(item)}
              </span>

              {/* Name and metadata */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: 'var(--obsidian-text-primary)'
                }}>
                  {item.name}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: 'var(--obsidian-text-secondary)',
                  marginTop: '1px'
                }}>
                  {item.isFolder && item.itemCount !== undefined && (
                    <span>{item.itemCount} item{item.itemCount !== 1 ? 's' : ''}</span>
                  )}
                  {!item.isFolder && item.size !== undefined && (
                    <span>{formatFileSize(item.size)}</span>
                  )}
                  {item.lastModified && (
                    <span> {formatDate(item.lastModified)}</span>
                  )}
                </div>
              </div>

              {/* Folder navigation indicator */}
              {item.isFolder && (
                <span
                  className="material-symbols-outlined"
                  style={{
                    fontSize: '16px',
                    color: 'var(--obsidian-text-disabled)',
                    flexShrink: 0
                  }}
                >
                  chevron_right
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SharePointFileExplorer;
